import { useState } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Play, X } from "lucide-react";

const VIDEO_SRC = "/swot-teaser.mp4";

// ── Inline Video Banner (for SWOT tool first-time) ──
// Layout: 9:16 video thumbnail on left, text on right
export const SwotVideoBanner = ({ onDismiss }) => {
  const [playing, setPlaying] = useState(false);

  return (
    <div className="rounded-xl overflow-hidden mb-6"
      style={{ background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)", border: "1px solid rgba(139,92,246,0.2)" }}
      data-testid="swot-video-banner">
      {playing ? (
        <div className="relative flex justify-center py-4"
          style={{ background: "rgba(0,0,0,0.4)" }}>
          <video
            src={VIDEO_SRC}
            autoPlay
            controls
            className="rounded-xl"
            style={{ aspectRatio: "9/16", maxHeight: 400, width: "auto" }}
            onEnded={() => setPlaying(false)}
            data-testid="swot-video-player"
          />
          <button onClick={() => { setPlaying(false); onDismiss?.(); }}
            className="absolute top-3 right-3 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors"
            data-testid="swot-video-close">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-5 p-5">
          <button onClick={() => setPlaying(true)}
            className="flex-shrink-0 w-20 rounded-xl bg-violet-500/10 border border-violet-400/20 flex items-center justify-center hover:bg-violet-500/20 transition-all group overflow-hidden"
            style={{ aspectRatio: "9/16" }}
            data-testid="swot-video-play">
            <Play className="w-7 h-7 text-violet-300 group-hover:text-white transition-colors ml-0.5" />
          </button>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-white mb-1">See how SWOT works</h3>
            <p className="text-xs text-slate-400 leading-relaxed">
              30-second overview — discover how the people who know you best can reveal your strengths and blind spots.
            </p>
          </div>
          <button onClick={onDismiss}
            className="text-xs text-slate-500 hover:text-slate-300 transition-colors flex-shrink-0 px-2 py-1"
            data-testid="swot-video-dismiss">
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
};

// ── Dashboard Nudge Card ──
// Modal: narrow & tall for 9:16 video
export const SwotVideoNudge = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button onClick={() => setOpen(true)}
        className="w-full rounded-xl p-4 text-left transition-all hover:shadow-sm group"
        style={{ background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)", border: "1px solid rgba(139,92,246,0.2)" }}
        data-testid="dashboard-video-nudge">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-violet-500/20 border border-violet-400/30 flex items-center justify-center group-hover:bg-violet-500/30 transition-all flex-shrink-0">
            <Play className="w-5 h-5 text-violet-300 ml-0.5" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-white">New to SWOT?</h4>
            <p className="text-xs text-slate-400">Watch a 30s explainer</p>
          </div>
        </div>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm p-0 bg-black border-0 rounded-xl overflow-hidden"
          data-testid="dashboard-video-modal">
          <video
            src={VIDEO_SRC}
            autoPlay
            controls
            className="w-full"
            style={{ aspectRatio: "9/16" }}
            onEnded={() => setOpen(false)}
            data-testid="dashboard-video-player"
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

// ── Landing Page Inline Video ──
// Centered 9:16 player with max-height, framed like a phone screen
export const LandingVideoSection = () => {
  const [playing, setPlaying] = useState(false);

  return (
    <div className="flex justify-center" data-testid="landing-video-section">
      <div className="relative rounded-2xl overflow-hidden"
        style={{ width: "min(300px, 80vw)", aspectRatio: "9/16", border: "1px solid rgba(255,255,255,0.1)", boxShadow: "0 0 60px rgba(139,92,246,0.15)" }}>
        {playing ? (
          <video
            src={VIDEO_SRC}
            autoPlay
            controls
            className="w-full h-full object-cover"
            onEnded={() => setPlaying(false)}
            data-testid="landing-video-player"
          />
        ) : (
          <button onClick={() => setPlaying(true)}
            className="w-full h-full bg-gradient-to-br from-slate-900 to-indigo-950 flex flex-col items-center justify-center gap-3 group cursor-pointer"
            data-testid="landing-video-play">
            <div className="w-16 h-16 rounded-full bg-violet-500/20 border border-violet-400/40 flex items-center justify-center group-hover:bg-violet-500/30 group-hover:scale-110 transition-all">
              <Play className="w-8 h-8 text-violet-300 group-hover:text-white transition-colors ml-1" />
            </div>
            <span className="text-sm text-slate-400 group-hover:text-slate-300 transition-colors">
              See how Evoluras works — 30s
            </span>
          </button>
        )}
      </div>
    </div>
  );
};
